/**
 * Direct-to-S3 upload engine for the quote file cards ("Data Upload Files" and "Purchase
 * Order & Miscellaneous Files").
 *
 * The store lives at module level, outside React: EditQuote remounts the Misc tab on every
 * quote reload and tab switch, so an upload held in component state would die with it. React
 * only subscribes (useSyncExternalStore) and re-renders the leaf that asked.
 *
 * Exported API (the UI is built on exactly this):
 *
 *   type RunKey = string;                          // `${quoteId}:${availableForSupplier ? "s" : "i"}`
 *   runKeyFor(quoteId: number, availableForSupplier: boolean): RunKey
 *
 *   interface RunSnapshot {
 *     state: "running" | "paused" | "done" | "authExpired";
 *     files: { id: string; name: string; path: string; size: number; sent: number;
 *              status: "queued" | "uploading" | "completing" | "done" | "error" | "canceled";
 *              error?: string;
 *              note?: string }[];                 // server remark on a saved file (folder gone,
 *                                                //   placed in the card root)
 *     totalBytes: number; sentBytes: number; bytesPerSecond: number;
 *     etaSeconds: number | null;                   // null = not known yet, or more than 24 h
 *     doneCount: number; errorCount: number;
 *     completedVersion: number;                    // bumps after every complete batch with an "ok"
 *   }                                              // and once more when the run ends
 *
 *   startRun({ quoteId, availableForSupplier, comment, targetDirectoryId, entries, directories }): void
 *     entries[i].path is the PARENT FOLDER of the file relative to the drop root ("" = card root
 *     or the target folder), exactly what scanDataTransfer/scanFileList return; directories may
 *     also list empty folders from a drop. A run already active under the same key is left alone.
 *   cancelRun(key: RunKey): void
 *     Aborts in-flight PUTs; abortQuoteFileUploads goes out only for queued/uploading/error
 *     files. Files in "completing" still finish. Works on a finished run too (releases the S3
 *     side of its failed files).
 *   retryFailed(key: RunKey): void
 *     Re-queues files in "error"; parts already on S3 are not re-sent, files without a session
 *     (init failed, or complete answered restart) start over, folder included. No-op after
 *     authExpired.
 *   useUploadRun(key: RunKey): RunSnapshot | undefined      // published <= 4/s plus on status changes
 *   useUploadSummary(key: RunKey): { active: boolean; errors: number }
 *     Separate low-frequency selector: its identity changes only when those two values do.
 *
 *   Non-hook access for tests and non-React code: subscribeRun, getRunSnapshot, getRunSummary.
 *   Pure helpers exported for tests: computeRate, etaFor, directoryPathsFor, chunk.
 */

import { useCallback, useSyncExternalStore } from "react";
import decode from "jwt-decode";
import { DocumentNode } from "graphql";
import putXhr, { getPutStatus, isUploadAbort } from "./putXhr";
import { cleanPath } from "./scanUpload";
import {
  ABORT_QUOTE_FILE_UPLOADS,
  COMPLETE_QUOTE_FILE_UPLOADS,
  ENSURE_QUOTE_FILE_DIRECTORIES,
  INIT_QUOTE_FILE_UPLOADS,
  SIGN_QUOTE_FILE_UPLOAD_PARTS,
} from "modules/Quotes/Services/Mutations/UploadMutations";

// ---------------------------------------------------------------------------------------------
// Tuning
// ---------------------------------------------------------------------------------------------

// S3 is HTTP/1.1 and browsers open 6 sockets per host: 4 PUTs leave room for GraphQL and previews
export const PARTS_IN_FLIGHT = 4;
export const PARTS_PER_FILE = 4;
export const INIT_BATCH = 25;
// Files that have an init but are not finished yet. Bounded so the presigned URLs of small
// files at the tail of a 2000-file queue do not expire, and a cancel does not leave hundreds
// of CreateMultipartUpload behind.
export const INIT_LOOKAHEAD = 100;
export const COMPLETE_BATCH = 5;
export const COMPLETE_FLUSH_MS = 2000;
export const DIRS_BATCH = 200;
// The server caps partNumbers per sign call at 50
export const SIGN_BATCH = 50;
export const SIGN_LOOKAHEAD = 20;
export const MAX_ATTEMPTS = 5;
export const BACKOFF_SECONDS = [1, 2, 4, 8, 16];
export const BACKOFF_JITTER = 0.25;
export const STALL_MS = 60000;
export const PUBLISH_MS = 250;
// While parts are in flight the snapshot is refreshed at least this often, so a stalled
// transfer shows its speed falling instead of freezing at the last value
export const HEARTBEAT_MS = 1000;
export const RATE_WINDOW_MS = 5000;
export const RATE_MIN_SAMPLES = 4;
export const RATE_MIN_SPAN_MS = 1500;
// Only for the JWT budget check at start: how slow an uplink we still plan for
export const ASSUMED_MIN_SPEED = 2 * 1024 * 1024;
export const JWT_MARGIN_SECONDS = 30 * 60;
// Mirrors QuoteFileUploadService::MAX_BYTES. The server rejects such a file per item as well;
// checking here saves the round trip and keeps it out of the JWT budget estimate.
export const MAX_FILE_BYTES = 20 * 1024 * 1024 * 1024;
export const ETA_HIDE_SECONDS = 24 * 3600;
// The server caps uploadTokens per abort call at 100
const ABORT_BATCH = 25;
// GraphQL transport failures worth repeating besides "no status at all" and 5xx: the request
// timed out on the way in, the server said too early or too many
const RETRIABLE_STATUSES = [408, 425, 429];
// fetch keepalive bodies are capped at 64 KB per page; leave room for the query text
const KEEPALIVE_BODY_LIMIT = 56 * 1024;

const AUTH_EXPIRED_MESSAGE =
  "Your session has expired: sign in again; the upload has to be restarted.";
const CORS_MESSAGE =
  "S3 refused the very first upload request (status 0). This usually means the bucket CORS " +
  "rules do not allow PUT from this site; the browser console has the details.";

// ---------------------------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------------------------

export type RunKey = string;

export type RunState = "running" | "paused" | "done" | "authExpired";

export type UploadFileStatus =
  | "queued"
  | "uploading"
  | "completing"
  | "done"
  | "error"
  | "canceled";

export interface RunFileSnapshot {
  id: string;
  name: string;
  path: string;
  size: number;
  sent: number;
  status: UploadFileStatus;
  error?: string;
  // Remark the server attached to a saved file, e.g. that its folder had disappeared and the
  // file was placed in the card root. Only ever set on "done".
  note?: string;
}

export interface RunSnapshot {
  state: RunState;
  files: RunFileSnapshot[];
  totalBytes: number;
  sentBytes: number;
  bytesPerSecond: number;
  etaSeconds: number | null;
  doneCount: number;
  errorCount: number;
  completedVersion: number;
}

export interface UploadSummary {
  active: boolean;
  errors: number;
}

export interface StartRunInput {
  quoteId: number;
  availableForSupplier: boolean;
  comment: string;
  targetDirectoryId: number | null;
  entries: { file: File; path: string }[];
  // Includes empty folders from a drop; the picker cannot deliver those
  directories: string[];
}

export interface RateSample {
  t: number;
  bytes: number;
}

export function runKeyFor(quoteId: number, availableForSupplier: boolean): RunKey {
  return `${quoteId}:${availableForSupplier ? "s" : "i"}`;
}

// ---------------------------------------------------------------------------------------------
// Internal model
// ---------------------------------------------------------------------------------------------

interface PartState {
  number: number;
  offset: number;
  size: number;
  url: string | null;
  done: boolean;
  inFlight: boolean;
  // Best progress seen for this part. Never lowered by a retry, so the bar does not roll back;
  // reset only when the server reports the part missing.
  loaded: number;
  attempts: number;
  retryAt: number;
  // The one free re-sign after a 403 has been used
  resigned: boolean;
  // Set right before a stall abort, to tell it from a cancel in the rejection handler
  stalled: boolean;
  xhr: XMLHttpRequest | null;
  stallTimer: ReturnType<typeof setTimeout> | null;
}

interface FileState {
  id: string;
  file: File;
  name: string;
  path: string;
  size: number;
  status: UploadFileStatus;
  error?: string;
  note?: string;
  directoryId: number | null;
  dirResolved: boolean;
  token: string | null;
  mode: "single" | "multipart" | null;
  contentType: string;
  parts: PartState[];
  inFlight: number;
  signInFlight: boolean;
  signAttempts: number;
  signRetryAt: number;
  completeAttempts: number;
  completeRetryAt: number;
  completeQueued: boolean;
  reopenCount: number;
  // Cached snapshot row, replaced only when something visible changed
  snap: RunFileSnapshot;
}

interface Run {
  key: RunKey;
  quoteId: number;
  availableForSupplier: boolean;
  comment: string;
  targetDirectoryId: number | null;
  files: FileState[];
  directories: string[];
  directoryIds: { [path: string]: number };
  // A retry put files without a session back to "queued": their folders must be ensured
  // again before the next init, also while the driver loop is already running
  dirsDirty: boolean;
  state: RunState;
  totalBytes: number;
  completedVersion: number;
  canceled: boolean;
  driving: boolean;
  wakeWaiting: (() => void) | null;
  wakePending: boolean;
  initInFlight: boolean;
  initAttempts: number;
  initRetryAt: number;
  completeInFlight: boolean;
  completeTimer: ReturnType<typeof setTimeout> | null;
  completeDue: boolean;
  samples: RateSample[];
  snapshot: RunSnapshot;
  summary: UploadSummary;
  publishTimer: ReturnType<typeof setTimeout> | null;
  publishDueAt: number;
}

interface PartUrl {
  partNumber: number;
  url: string;
}

interface InitResult {
  clientRef: string;
  // "error", like a missing uploadToken, means the server rejected this one file; message
  // then says why and its batch mates are unaffected
  status: "ok" | "error" | null;
  uploadToken: string | null;
  message: string | null;
  mode: "single" | "multipart";
  key: string;
  contentType: string | null;
  partSize: number;
  partCount: number;
  urls: PartUrl[] | null;
  ttlSeconds: number;
}

interface CompleteResult {
  clientRef: string;
  status: "ok" | "error" | "retry";
  // On "ok" a warning about the saved file (folder gone, placed in the card root); otherwise
  // the reason for the status
  message: string | null;
  missingParts: number[] | null;
  // The session is dead (object gone, size mismatch, INSERT failed): another complete can
  // never succeed, the file has to be initialised and uploaded again
  restart: boolean | null;
}

interface ApolloLikeError {
  message?: string;
  // result: the parsed response body Apollo's ServerError carries for a non-2xx status
  networkError?: { statusCode?: number; message?: string; result?: unknown } | null;
  graphQLErrors?: unknown[];
}

// ---------------------------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------------------------

const runs: { [key: string]: Run } = {};
const listeners: { [key: string]: Array<() => void> } = {};
const IDLE_SUMMARY: UploadSummary = { active: false, errors: 0 };
// The PUT semaphore is shared by every run: both cards of a quote go through the same 6 sockets
let inFlightGlobal = 0;
// Distinguishes "CORS is not configured" (nothing ever got through) from a flaky network
let anyPutSucceeded = false;
let pageHooksInstalled = false;

// ---------------------------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------------------------

export function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

function sleep(ms: number): Promise<void> {
  return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

function backoffMs(attempt: number): number {
  const index = Math.min(Math.max(attempt, 1), BACKOFF_SECONDS.length) - 1;
  const jitter = 1 - BACKOFF_JITTER + Math.random() * BACKOFF_JITTER * 2;
  return Math.round(BACKOFF_SECONDS[index] * 1000 * jitter);
}

function isPending(f: FileState): boolean {
  return f.status === "queued" || f.status === "uploading" || f.status === "completing";
}

function isActiveState(state: RunState): boolean {
  return state === "running" || state === "paused";
}

function allPartsDone(f: FileState): boolean {
  for (let i = 0; i < f.parts.length; i++) if (!f.parts[i].done) return false;
  return true;
}

function asApolloError(e: unknown): ApolloLikeError {
  return (e && typeof e === "object" ? e : {}) as ApolloLikeError;
}

function isAuthError(e: unknown): boolean {
  const networkError = asApolloError(e).networkError;
  if (!networkError) return false;
  return (
    networkError.statusCode === 401 ||
    String(networkError.message || "").slice(-3) === "401"
  );
}

// Transport failed (no status), or the server answered "not now" (408, 425, 429, 5xx): the
// same request may simply be repeated. Any other HTTP status (400, 403, 404, 413...) is a
// definite no, like a GraphQL error; repeating either changes nothing.
function isRetriableError(e: unknown): boolean {
  if (isAuthError(e)) return false;
  const networkError = asApolloError(e).networkError;
  if (!networkError) return false;
  const status = networkError.statusCode;
  if (typeof status !== "number" || status === 0) return true;
  return status >= 500 || RETRIABLE_STATUSES.indexOf(status) !== -1;
}

// Prefers the message from the response body: Apollo's ServerError keeps the parsed JSON body
// in networkError.result, and that is where the server puts the reason for a 403 or a 400.
function errorMessage(e: unknown, fallback: string): string {
  const error = asApolloError(e);
  const result = error.networkError ? error.networkError.result : undefined;
  if (result && typeof result === "object") {
    const serverMessage = (result as { message?: unknown }).message;
    if (typeof serverMessage === "string" && serverMessage) return serverMessage;
  }
  const message = error.message;
  return message ? String(message) : fallback;
}

function readJwt(): string | null {
  try {
    return localStorage.token || sessionStorage.token || null;
  } catch (e) {
    return null;
  }
}

function readJwtExp(): number | null {
  const token = readJwt();
  if (!token) return null;
  try {
    const exp = decode<{ exp?: unknown }>(token).exp;
    return typeof exp === "number" ? exp : null;
  } catch (e) {
    return null;
  }
}

/**
 * The union of the scanner's folders and every parent folder of the entries, with all their
 * ancestors, parents before children (also across DIRS_BATCH chunks: depth first, then name).
 */
export function directoryPathsFor(
  entries: { path: string }[],
  directories: string[],
): string[] {
  const seen: { [path: string]: true } = {};
  const out: string[] = [];
  const add = (raw: string) => {
    const path = cleanPath(raw || "");
    if (!path) return;
    const segments = path.split("/");
    for (let i = 1; i <= segments.length; i++) {
      const ancestor = segments.slice(0, i).join("/");
      if (!seen[ancestor]) {
        seen[ancestor] = true;
        out.push(ancestor);
      }
    }
  };
  directories.forEach(add);
  entries.forEach((entry) => add(entry.path));
  out.sort((a, b) => {
    const depthA = a.split("/").length;
    const depthB = b.split("/").length;
    if (depthA !== depthB) return depthA - depthB;
    if (a === b) return 0;
    return a < b ? -1 : 1;
  });
  return out;
}

/** Bytes per second over the last RATE_WINDOW_MS; 0 until there is enough to go on. */
export function computeRate(samples: RateSample[], now: number): number {
  const from = now - RATE_WINDOW_MS;
  let first = -1;
  for (let i = 0; i < samples.length; i++) {
    if (samples[i].t >= from) {
      first = i;
      break;
    }
  }
  if (first === -1) return 0;
  const a = samples[first];
  const b = samples[samples.length - 1];
  const span = b.t - a.t;
  if (samples.length - first < RATE_MIN_SAMPLES || span < RATE_MIN_SPAN_MS) return 0;
  const bytes = b.bytes - a.bytes;
  return bytes > 0 ? (bytes * 1000) / span : 0;
}

/** Seconds left, or null when the speed is unknown or the estimate is beyond 24 h. */
export function etaFor(remainingBytes: number, bytesPerSecond: number): number | null {
  if (remainingBytes <= 0) return 0;
  if (bytesPerSecond <= 0) return null;
  const seconds = Math.ceil(remainingBytes / bytesPerSecond);
  return seconds > ETA_HIDE_SECONDS ? null : seconds;
}

// ---------------------------------------------------------------------------------------------
// GraphQL transport
// ---------------------------------------------------------------------------------------------

type Variables = { [key: string]: unknown };

async function mutate<T>(mutation: DocumentNode, variables: Variables): Promise<T> {
  // Lazy on purpose: Services/ClientConfig.ts imports uploadFlags for its errorLink, and a
  // static import here would put the engine (and React) into the client's module graph.
  const { client } = await import("Services/ClientConfig");
  const result = await client.mutate<T>({
    mutation,
    variables,
    fetchPolicy: "no-cache",
  });
  if (!result.data) throw new Error("Empty response from the server.");
  return result.data;
}

// ---------------------------------------------------------------------------------------------
// Store: subscriptions and snapshots
// ---------------------------------------------------------------------------------------------

export function subscribeRun(key: RunKey, callback: () => void): () => void {
  (listeners[key] = listeners[key] || []).push(callback);
  return () => {
    const list = listeners[key];
    if (!list) return;
    const index = list.indexOf(callback);
    if (index !== -1) list.splice(index, 1);
    if (!list.length) delete listeners[key];
  };
}

export function getRunSnapshot(key: RunKey): RunSnapshot | undefined {
  const run = runs[key];
  return run ? run.snapshot : undefined;
}

export function getRunSummary(key: RunKey): UploadSummary {
  const run = runs[key];
  return run ? run.summary : IDLE_SUMMARY;
}

function notify(key: RunKey): void {
  const list = listeners[key];
  if (!list) return;
  list.slice().forEach((callback) => callback());
}

function fileSent(f: FileState): number {
  if (f.status === "done") return f.size;
  let sent = 0;
  for (let i = 0; i < f.parts.length; i++) {
    const part = f.parts[i];
    sent += part.done ? part.size : part.loaded;
  }
  return sent > f.size ? f.size : sent;
}

function pushSample(run: Run, now: number, bytes: number): void {
  run.samples.push({ t: now, bytes });
  // Keep a little more than the window so the first in-window sample has a predecessor
  const cutoff = now - RATE_WINDOW_MS * 2;
  let drop = 0;
  while (drop < run.samples.length - 1 && run.samples[drop].t < cutoff) drop++;
  if (drop) run.samples.splice(0, drop);
}

function clearPublishTimer(run: Run): void {
  if (run.publishTimer !== null) {
    clearTimeout(run.publishTimer);
    run.publishTimer = null;
  }
  run.publishDueAt = 0;
}

function schedulePublish(run: Run, delay: number): void {
  const dueAt = Date.now() + delay;
  if (run.publishTimer !== null && run.publishDueAt <= dueAt) return;
  clearPublishTimer(run);
  run.publishDueAt = dueAt;
  run.publishTimer = setTimeout(() => {
    run.publishTimer = null;
    run.publishDueAt = 0;
    publishNow(run);
  }, delay);
}

/** Progress ticks are coalesced: at most one snapshot per PUBLISH_MS. */
function markProgress(run: Run): void {
  schedulePublish(run, PUBLISH_MS);
}

/** Status changes publish immediately; the snapshot object is replaced only here. */
function publishNow(run: Run): void {
  clearPublishTimer(run);
  const now = Date.now();
  const rows: RunFileSnapshot[] = new Array(run.files.length);
  let sent = 0;
  let remaining = 0;
  let doneCount = 0;
  let errorCount = 0;
  let inFlight = 0;
  for (let i = 0; i < run.files.length; i++) {
    const f = run.files[i];
    const fileBytes = fileSent(f);
    const prev = f.snap;
    if (
      prev.sent !== fileBytes ||
      prev.status !== f.status ||
      prev.error !== f.error ||
      prev.note !== f.note
    ) {
      f.snap = {
        id: f.id,
        name: f.name,
        path: f.path,
        size: f.size,
        sent: fileBytes,
        status: f.status,
        error: f.error,
        note: f.note,
      };
    }
    rows[i] = f.snap;
    sent += fileBytes;
    inFlight += f.inFlight;
    if (f.status === "done") doneCount++;
    else if (f.status === "error") errorCount++;
    if (isPending(f)) remaining += f.size - fileBytes;
  }

  const running = run.state === "running";
  if (running) pushSample(run, now, sent);
  else run.samples = [];
  const rate = running ? computeRate(run.samples, now) : 0;

  run.snapshot = {
    state: run.state,
    files: rows,
    totalBytes: run.totalBytes,
    sentBytes: sent,
    bytesPerSecond: rate,
    etaSeconds: running ? etaFor(remaining, rate) : null,
    doneCount,
    errorCount,
    completedVersion: run.completedVersion,
  };
  const active = isActiveState(run.state);
  if (run.summary.active !== active || run.summary.errors !== errorCount) {
    run.summary = { active, errors: errorCount };
  }
  if (running && inFlight > 0) schedulePublish(run, HEARTBEAT_MS);
  notify(run.key);
}

// ---------------------------------------------------------------------------------------------
// Driver: one loop per run, woken by events
// ---------------------------------------------------------------------------------------------

function wake(run: Run): void {
  const waiting = run.wakeWaiting;
  if (waiting) {
    run.wakeWaiting = null;
    waiting();
  } else {
    run.wakePending = true;
  }
}

function waitForWake(run: Run): Promise<void> {
  if (run.wakePending) {
    run.wakePending = false;
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    run.wakeWaiting = resolve;
  });
}

function wakeAfter(run: Run, ms: number): void {
  setTimeout(() => wake(run), ms);
}

function isRunSettled(run: Run): boolean {
  if (run.initInFlight || run.completeInFlight) return false;
  for (let i = 0; i < run.files.length; i++) if (isPending(run.files[i])) return false;
  return true;
}

function clearRunTimers(run: Run): void {
  clearPublishTimer(run);
  if (run.completeTimer !== null) {
    clearTimeout(run.completeTimer);
    run.completeTimer = null;
  }
  run.completeDue = false;
}

async function driveRun(run: Run): Promise<void> {
  if (run.driving) return;
  run.driving = true;
  try {
    await prepare(run);
    while (run.state !== "authExpired" && !isRunSettled(run)) {
      if (run.state === "running") {
        if (!run.canceled) {
          if (run.dirsDirty) await resolveDirectories(run);
          maybeInit(run);
          pumpAll();
        }
        maybeFlushCompletes(run);
      }
      await waitForWake(run);
    }
    if (run.state !== "authExpired") finishRun(run);
  } catch (e) {
    // A bug in the scheduler must not leave the run spinning forever behind a live beforeunload
    const message = errorMessage(e, "Unexpected upload error.");
    run.files.forEach((f) => failFile(run, f, message));
    finishRun(run);
  } finally {
    run.driving = false;
  }
}

function finishRun(run: Run): void {
  clearRunTimers(run);
  run.state = "done";
  run.completedVersion++;
  publishNow(run);
}

// ---------------------------------------------------------------------------------------------
// Stage 1-2: JWT budget and folders
// ---------------------------------------------------------------------------------------------

async function prepare(run: Run): Promise<void> {
  // Per-file limits go first and fail only the offender: the server applies the same rules
  // per item, but a 25 GB file must not inflate the session budget below for everything else.
  // Same wording as the server, so the row reads the same whichever side caught it.
  run.files.forEach((f) => {
    if (f.status !== "queued" || f.token) return;
    if (f.size > MAX_FILE_BYTES) failFile(run, f, `File "${f.name}" is too large (max 20 GB).`);
    else if (!f.name.trim()) failFile(run, f, "File name missing.");
  });
  const exp = readJwtExp();
  if (exp === null) {
    // Without a token every GraphQL call would 401 anyway; say so before touching anything
    expireRun(run);
    return;
  }
  const shortfall = jwtBudgetShortfall(run, exp, Date.now() / 1000);
  if (shortfall) {
    // Refuse rather than warn: there is no refresh token, and the sign-in page kills the store
    // and the File handles with it, so an upload cut by an expired JWT cannot be resumed.
    run.files.forEach((f) => {
      if (f.status === "queued" && !f.token) failFile(run, f, shortfall);
    });
    return;
  }
  await resolveDirectories(run);
}

function jwtBudgetShortfall(run: Run, exp: number, nowSeconds: number): string | null {
  let remaining = 0;
  run.files.forEach((f) => {
    if (f.status === "queued" && !f.token) remaining += f.size;
  });
  const needSeconds = remaining / ASSUMED_MIN_SPEED + JWT_MARGIN_SECONDS;
  const leftSeconds = exp - nowSeconds;
  if (leftSeconds >= needSeconds) return null;
  return (
    `Your session expires in about ${Math.max(0, Math.round(leftSeconds / 60))} min, but this ` +
    `upload needs about ${Math.ceil(needSeconds / 60)} min at a conservative speed: ` +
    "sign out and back in before this upload."
  );
}

async function resolveDirectories(run: Run): Promise<void> {
  run.dirsDirty = false;
  const pending = run.directories.filter(
    (path) => typeof run.directoryIds[path] !== "number",
  );
  const chunks = chunk(pending, DIRS_BATCH);
  let failure: string | null = null;
  // Sequential on purpose: a chunk may create the parents the next chunk builds on
  for (let i = 0; i < chunks.length && failure === null; i++) {
    if (run.canceled || run.state === "authExpired") return;
    failure = await ensureDirectoryChunk(run, chunks[i]);
  }
  if (run.state === "authExpired") return;
  run.files.forEach((f) => {
    if (f.dirResolved || f.status !== "queued") return;
    if (!f.path) {
      f.directoryId = run.targetDirectoryId;
      f.dirResolved = true;
      return;
    }
    const id = run.directoryIds[f.path];
    if (typeof id === "number") {
      f.directoryId = id;
      f.dirResolved = true;
      return;
    }
    failFile(run, f, failure || "The folder for this file could not be created.");
  });
  publishNow(run);
}

/** Resolves to null on success, otherwise to the message the affected files get. */
async function ensureDirectoryChunk(run: Run, paths: string[]): Promise<string | null> {
  for (let attempt = 1; ; attempt++) {
    try {
      const data = await mutate<{
        ensureQuoteFileDirectories: { path: string; directoryId: number }[] | null;
      }>(ENSURE_QUOTE_FILE_DIRECTORIES, {
        quoteId: run.quoteId,
        availableForSupplier: run.availableForSupplier,
        parentId: run.targetDirectoryId,
        paths,
      });
      (data.ensureQuoteFileDirectories || []).forEach((row) => {
        if (row && row.path && typeof row.directoryId === "number") {
          run.directoryIds[row.path] = row.directoryId;
        }
      });
      return null;
    } catch (e) {
      if (isAuthError(e)) {
        expireAllRuns();
        return AUTH_EXPIRED_MESSAGE;
      }
      if (!isRetriableError(e) || attempt >= MAX_ATTEMPTS) {
        return errorMessage(e, "Could not create the folders.");
      }
      await sleep(backoffMs(attempt));
      if (run.canceled) return "Canceled.";
    }
  }
}

// ---------------------------------------------------------------------------------------------
// Stage 3: init, in a sliding window
// ---------------------------------------------------------------------------------------------

function maybeInit(run: Run): void {
  if (run.initInFlight || Date.now() < run.initRetryAt) return;
  let active = 0;
  const candidates: FileState[] = [];
  for (let i = 0; i < run.files.length; i++) {
    const f = run.files[i];
    if (f.token && isPending(f)) active++;
    else if (
      f.status === "queued" &&
      !f.token &&
      f.dirResolved &&
      candidates.length < INIT_BATCH
    ) {
      candidates.push(f);
    }
  }
  const room = INIT_LOOKAHEAD - active;
  if (room <= 0 || !candidates.length) return;
  const batch = candidates.slice(0, room);

  run.initInFlight = true;
  mutate<{ initQuoteFileUploads: InitResult[] | null }>(INIT_QUOTE_FILE_UPLOADS, {
    quoteId: run.quoteId,
    availableForSupplier: run.availableForSupplier,
    comment: run.comment,
    files: batch.map((f) => ({
      clientRef: f.id,
      fileName: f.name,
      fileSize: f.size,
      directoryId: f.directoryId,
    })),
  }).then(
    (data) => {
      run.initInFlight = false;
      run.initAttempts = 0;
      applyInit(run, batch, data.initQuoteFileUploads || []);
      pumpAll();
      wake(run);
    },
    (e) => {
      run.initInFlight = false;
      if (isAuthError(e)) {
        expireAllRuns();
        return;
      }
      if (isRetriableError(e) && run.initAttempts < MAX_ATTEMPTS) {
        run.initAttempts++;
        const delay = backoffMs(run.initAttempts);
        run.initRetryAt = Date.now() + delay;
        wakeAfter(run, delay);
      } else if (isRetriableError(e)) {
        // The server is unreachable: failing 25 files per 31 s would take an hour for a big
        // folder. Fail everything that has not started; Retry failed picks it all up again.
        run.initAttempts = 0;
        const message = errorMessage(e, "The server could not be reached.");
        run.files.forEach((f) => {
          if (f.status === "queued" && !f.token) failFile(run, f, message);
        });
      } else {
        const message = errorMessage(e, "Could not start the upload.");
        batch.forEach((f) => failFile(run, f, message));
      }
      wake(run);
    },
  );
}

function newPart(number: number, offset: number, size: number, url: string | null): PartState {
  return {
    number,
    offset,
    size,
    url,
    done: false,
    inFlight: false,
    loaded: 0,
    attempts: 0,
    retryAt: 0,
    resigned: false,
    stalled: false,
    xhr: null,
    stallTimer: null,
  };
}

/** Returns a problem description, or null when the file is ready to transfer. */
function buildParts(f: FileState, init: InitResult): string | null {
  const urls: { [partNumber: number]: string } = {};
  (init.urls || []).forEach((row) => {
    if (row && typeof row.partNumber === "number" && row.url) urls[row.partNumber] = row.url;
  });
  const parts: PartState[] = [];
  if (init.mode === "single") {
    parts.push(newPart(1, 0, f.size, urls[1] || null));
  } else if (init.mode === "multipart") {
    const partSize = Number(init.partSize);
    const partCount = Number(init.partCount);
    const consistent =
      partSize > 0 &&
      partCount >= 1 &&
      (partCount - 1) * partSize < f.size &&
      partCount * partSize >= f.size;
    if (!consistent) return "Unexpected upload session from the server.";
    for (let n = 1; n <= partCount; n++) {
      const offset = (n - 1) * partSize;
      parts.push(newPart(n, offset, Math.min(partSize, f.size - offset), urls[n] || null));
    }
  } else {
    return "Unexpected upload mode from the server.";
  }
  f.parts = parts;
  f.mode = init.mode;
  f.token = init.uploadToken;
  f.contentType = init.contentType || "";
  return null;
}

function applyInit(run: Run, batch: FileState[], rows: InitResult[]): void {
  const byRef: { [clientRef: string]: InitResult } = {};
  rows.forEach((row) => {
    if (row && row.clientRef) byRef[row.clientRef] = row;
  });
  const unusedTokens: string[] = [];
  batch.forEach((f) => {
    const row = byRef[f.id];
    if (f.status !== "queued") {
      // Canceled while the init was in flight: release what the server just created
      if (row && row.uploadToken) unusedTokens.push(row.uploadToken);
      return;
    }
    if (!row || row.status === "error" || !row.uploadToken) {
      // A rejection of this file alone (size, name, folder gone); its batch mates went through
      failFile(
        run,
        f,
        (row && row.message) || "The server returned no upload session for this file.",
      );
      return;
    }
    const problem = buildParts(f, row);
    if (problem) {
      unusedTokens.push(row.uploadToken);
      failFile(run, f, problem);
    }
  });
  sendAbort(unusedTokens);
  publishNow(run);
}

// ---------------------------------------------------------------------------------------------
// Stage 4-5: transfer, signing, retries
// ---------------------------------------------------------------------------------------------

function pumpAll(): void {
  const keys = Object.keys(runs);
  for (let i = 0; i < keys.length && inFlightGlobal < PARTS_IN_FLIGHT; i++) {
    const run = runs[keys[i]];
    if (run.state === "running" && !run.canceled) pumpRun(run);
  }
}

function pumpRun(run: Run): void {
  const now = Date.now();
  for (let i = 0; i < run.files.length && inFlightGlobal < PARTS_IN_FLIGHT; i++) {
    const f = run.files[i];
    if (!f.token || (f.status !== "queued" && f.status !== "uploading")) continue;
    maybeSign(run, f);
    while (inFlightGlobal < PARTS_IN_FLIGHT && f.inFlight < PARTS_PER_FILE) {
      const part = nextPart(f, now);
      if (!part) break;
      startPut(run, f, part);
    }
  }
}

function nextPart(f: FileState, now: number): PartState | null {
  for (let i = 0; i < f.parts.length; i++) {
    const part = f.parts[i];
    if (!part.done && !part.inFlight && part.url && part.retryAt <= now) return part;
  }
  return null;
}

/** Keeps SIGN_LOOKAHEAD signed parts ahead of the sender; also serves the 403 re-sign. */
function maybeSign(run: Run, f: FileState): void {
  if (f.signInFlight || !f.token || Date.now() < f.signRetryAt) return;
  const unsigned: number[] = [];
  let signedWaiting = 0;
  for (let i = 0; i < f.parts.length; i++) {
    const part = f.parts[i];
    if (part.done || part.inFlight) continue;
    if (part.url) signedWaiting++;
    else if (unsigned.length < SIGN_BATCH) unsigned.push(part.number);
  }
  if (!unsigned.length || signedWaiting >= SIGN_LOOKAHEAD) return;

  f.signInFlight = true;
  mutate<{ signQuoteFileUploadParts: PartUrl[] | null }>(SIGN_QUOTE_FILE_UPLOAD_PARTS, {
    uploadToken: f.token,
    partNumbers: unsigned,
  }).then(
    (data) => {
      f.signInFlight = false;
      f.signAttempts = 0;
      (data.signQuoteFileUploadParts || []).forEach((row) => {
        if (!row || typeof row.partNumber !== "number" || !row.url) return;
        const part = f.parts[row.partNumber - 1];
        if (part && part.number === row.partNumber && !part.done) part.url = row.url;
      });
      pumpAll();
      wake(run);
    },
    (e) => {
      f.signInFlight = false;
      if (isAuthError(e)) {
        expireAllRuns();
        return;
      }
      if (isRetriableError(e) && f.signAttempts < MAX_ATTEMPTS) {
        f.signAttempts++;
        const delay = backoffMs(f.signAttempts);
        f.signRetryAt = Date.now() + delay;
        wakeAfter(run, delay);
      } else {
        failFile(run, f, errorMessage(e, "Could not sign the upload."));
      }
      wake(run);
    },
  );
}

function armStall(part: PartState): void {
  if (part.stallTimer !== null) clearTimeout(part.stallTimer);
  part.stallTimer = setTimeout(() => {
    part.stallTimer = null;
    if (!part.inFlight || !part.xhr) return;
    part.stalled = true;
    part.xhr.abort();
  }, STALL_MS);
}

function releasePut(f: FileState, part: PartState): void {
  if (part.stallTimer !== null) {
    clearTimeout(part.stallTimer);
    part.stallTimer = null;
  }
  part.inFlight = false;
  part.xhr = null;
  f.inFlight--;
  inFlightGlobal--;
}

function startPut(run: Run, f: FileState, part: PartState): void {
  part.inFlight = true;
  part.stalled = false;
  part.xhr = null;
  f.inFlight++;
  inFlightGlobal++;
  if (f.status === "queued") {
    f.status = "uploading";
    publishNow(run);
  }
  // single: the type is informative only, the server sets the authoritative one on finalize.
  // multipart: no type on purpose, so XHR adds no Content-Type header to the signed request.
  const body =
    f.mode === "single"
      ? f.file.slice(0, f.size, f.contentType)
      : f.file.slice(part.offset, part.offset + part.size);
  armStall(part);
  putXhr({
    url: part.url as string,
    body,
    onProgress: (loaded) => {
      const capped = loaded > part.size ? part.size : loaded;
      if (capped > part.loaded) part.loaded = capped;
      armStall(part);
      markProgress(run);
    },
    onOpen: (xhr) => {
      part.xhr = xhr;
    },
  }).then(
    () => onPutDone(run, f, part),
    (e) => onPutFailed(run, f, part, e),
  );
}

function onPutDone(run: Run, f: FileState, part: PartState): void {
  releasePut(f, part);
  anyPutSucceeded = true;
  part.done = true;
  part.loaded = part.size;
  part.attempts = 0;
  if (f.status === "uploading" && allPartsDone(f)) enqueueComplete(run, f);
  else markProgress(run);
  pumpAll();
  wake(run);
}

function onPutFailed(run: Run, f: FileState, part: PartState, e: unknown): void {
  releasePut(f, part);
  const stalled = part.stalled;
  part.stalled = false;
  const finish = () => {
    pumpAll();
    wake(run);
  };
  // A plain abort means cancel, pause or auth expiry already moved the file on
  if ((isUploadAbort(e) && !stalled) || f.status !== "uploading") {
    finish();
    return;
  }
  const status = stalled ? 0 : getPutStatus(e);
  if (
    status === 0 &&
    !stalled &&
    typeof navigator !== "undefined" &&
    navigator.onLine === false
  ) {
    // Not the part's fault: park the run and resend when the browser is back online
    pauseRun(run);
    finish();
    return;
  }
  if (status === 403) {
    // The URL is what expired; a fresh one comes from sign (partNumbers [1] for single PUT)
    part.url = null;
    if (!part.resigned) {
      part.resigned = true;
      finish();
      return;
    }
  }
  part.attempts++;
  if (part.attempts < MAX_ATTEMPTS) {
    const delay = backoffMs(part.attempts);
    part.retryAt = Date.now() + delay;
    wakeAfter(run, delay);
    finish();
    return;
  }
  const corsSuspected = status === 0 && !stalled && !anyPutSucceeded;
  let message: string;
  if (corsSuspected) message = CORS_MESSAGE;
  else if (stalled) message = `No upload progress for ${STALL_MS / 1000} s, ${MAX_ATTEMPTS} attempts.`;
  else if (status === 0) message = `The connection to S3 dropped ${MAX_ATTEMPTS} times.`;
  else message = `S3 answered ${status}, ${MAX_ATTEMPTS} attempts.`;
  failFile(run, f, message);
  // A CORS misconfiguration is not per file: stop the run instead of failing 2000 files one by one
  if (corsSuspected) failRun(run, message);
  finish();
}

function abortParts(f: FileState): void {
  for (let i = 0; i < f.parts.length; i++) {
    const part = f.parts[i];
    if (part.inFlight && part.xhr) part.xhr.abort();
  }
}

function failFile(run: Run, f: FileState, message: string): void {
  if (!isPending(f)) return;
  abortParts(f);
  f.status = "error";
  f.error = message;
  f.completeQueued = false;
  publishNow(run);
}

function failRun(run: Run, message: string): void {
  run.files.forEach((f) => {
    if (f.status === "queued" || f.status === "uploading") failFile(run, f, message);
  });
}

function pauseRun(run: Run): void {
  if (run.state !== "running") return;
  run.state = "paused";
  // The parts keep their URL and progress and are resent on resume without an attempt counted
  run.files.forEach(abortParts);
  publishNow(run);
}

function resumeRun(run: Run): void {
  if (run.state !== "paused") return;
  run.state = "running";
  publishNow(run);
  wake(run);
}

// ---------------------------------------------------------------------------------------------
// Stage 6: complete
// ---------------------------------------------------------------------------------------------

function enqueueComplete(run: Run, f: FileState): void {
  f.status = "completing";
  f.completeQueued = true;
  f.completeRetryAt = 0;
  publishNow(run);
}

function armCompleteTimer(run: Run): void {
  if (run.completeTimer !== null) return;
  run.completeTimer = setTimeout(() => {
    run.completeTimer = null;
    run.completeDue = true;
    wake(run);
  }, COMPLETE_FLUSH_MS);
}

function maybeFlushCompletes(run: Run): void {
  if (run.completeInFlight) return;
  const now = Date.now();
  const ready: FileState[] = [];
  let transferring = false;
  for (let i = 0; i < run.files.length; i++) {
    const f = run.files[i];
    if (f.status === "queued" || f.status === "uploading") transferring = true;
    else if (f.status === "completing" && f.completeQueued && f.completeRetryAt <= now) {
      ready.push(f);
    }
  }
  if (!ready.length) return;
  // Batches of COMPLETE_BATCH, or whatever is there after COMPLETE_FLUSH_MS; no reason to wait
  // once nothing else is transferring or the run was canceled
  if (ready.length < COMPLETE_BATCH && transferring && !run.completeDue && !run.canceled) {
    armCompleteTimer(run);
    return;
  }
  if (run.completeTimer !== null) {
    clearTimeout(run.completeTimer);
    run.completeTimer = null;
  }
  run.completeDue = false;

  const batch = ready.slice(0, COMPLETE_BATCH);
  batch.forEach((f) => {
    f.completeQueued = false;
  });
  run.completeInFlight = true;
  mutate<{ completeQuoteFileUploads: CompleteResult[] | null }>(COMPLETE_QUOTE_FILE_UPLOADS, {
    uploads: batch.map((f) => ({ uploadToken: f.token })),
  }).then(
    (data) => {
      run.completeInFlight = false;
      applyComplete(run, batch, data.completeQuoteFileUploads || []);
      wake(run);
    },
    (e) => {
      run.completeInFlight = false;
      if (isAuthError(e)) {
        expireAllRuns();
        return;
      }
      if (isRetriableError(e)) {
        const message = errorMessage(e, "Network error while finalizing the upload.");
        batch.forEach((f) => retryComplete(run, f, message));
      } else {
        const message = errorMessage(e, "Could not finalize the upload.");
        batch.forEach((f) => failFile(run, f, message));
      }
      publishNow(run);
      wake(run);
    },
  );
}

function retryComplete(run: Run, f: FileState, message: string): void {
  if (f.status !== "completing") return;
  f.completeAttempts++;
  if (f.completeAttempts >= MAX_ATTEMPTS) {
    failFile(run, f, message);
    return;
  }
  const delay = backoffMs(f.completeAttempts);
  f.completeQueued = true;
  f.completeRetryAt = Date.now() + delay;
  wakeAfter(run, delay);
}

/** Puts the parts S3 does not have back into the queue; returns how many were reopened. */
function reopenParts(f: FileState, numbers: number[]): number {
  let reopened = 0;
  numbers.forEach((n) => {
    const part = f.parts[n - 1];
    if (!part || part.number !== n || !part.done) return;
    part.done = false;
    part.loaded = 0;
    part.attempts = 0;
    part.retryAt = 0;
    part.resigned = false;
    reopened++;
  });
  return reopened;
}

function applyComplete(run: Run, batch: FileState[], rows: CompleteResult[]): void {
  const byRef: { [clientRef: string]: CompleteResult } = {};
  rows.forEach((row) => {
    if (row && row.clientRef) byRef[row.clientRef] = row;
  });
  let anyOk = false;
  const abortTokens: string[] = [];
  batch.forEach((f) => {
    if (f.status !== "completing") return;
    const row = byRef[f.id];
    if (!row) {
      retryComplete(run, f, "The server returned no result for this file.");
      return;
    }
    if (row.status === "ok") {
      f.status = "done";
      f.error = undefined;
      // A message on an ok row is a remark about the saved file, not a failure
      f.note = row.message || undefined;
      // Nothing may ever abort a saved file: forget the token so cancel cannot pick it up
      f.token = null;
      anyOk = true;
      return;
    }
    if (row.restart === true) {
      // The session is dead: drop it so retryFailed inits and uploads the file again (the
      // folder stays resolved). The token goes nowhere, not even to abort: the server has
      // already removed whatever was behind it.
      f.token = null;
      f.parts = [];
      f.mode = null;
      failFile(run, f, row.message || "The upload has to be started over.");
      return;
    }
    if (row.status === "retry") {
      retryComplete(run, f, row.message || "The server asked to retry.");
      return;
    }
    const missing = (row.missingParts || []).filter((n) => typeof n === "number");
    if (missing.length && run.canceled) {
      // Cancel arrived while this complete was in flight. Reopening the parts would leave the
      // file "uploading" in a run whose driver no longer pumps, so it would never settle:
      // cancel it like the others and release its S3 side.
      if (f.token) abortTokens.push(f.token);
      f.token = null;
      f.status = "canceled";
      f.error = undefined;
      f.completeQueued = false;
      return;
    }
    if (missing.length && f.reopenCount < 2 && reopenParts(f, missing) > 0) {
      f.reopenCount++;
      f.completeAttempts = 0;
      f.status = "uploading";
      return;
    }
    failFile(run, f, row.message || "The server could not finalize the file.");
  });
  sendAbort(abortTokens);
  if (anyOk) run.completedVersion++;
  publishNow(run);
}

// ---------------------------------------------------------------------------------------------
// Abort, auth expiry, page hooks
// ---------------------------------------------------------------------------------------------

function sendAbort(tokens: string[]): void {
  if (!tokens.length) return;
  chunk(tokens, ABORT_BATCH).forEach((batch) => {
    mutate<{ abortQuoteFileUploads: number | null }>(ABORT_QUOTE_FILE_UPLOADS, {
      uploadTokens: batch,
    }).then(
      () => undefined,
      (e) => {
        // Best effort: the bucket lifecycle rules sweep whatever this did not reach
        console.warn(`[upload] abort failed: ${errorMessage(e, "unknown error")}`);
      },
    );
  });
}

/** Tokens the abort mutation may receive: never those of done or completing files. */
function abortableTokens(run: Run): string[] {
  const tokens: string[] = [];
  run.files.forEach((f) => {
    if (
      f.token &&
      (f.status === "queued" || f.status === "uploading" || f.status === "error")
    ) {
      tokens.push(f.token);
    }
  });
  return tokens;
}

function expireRun(run: Run): void {
  if (!isActiveState(run.state)) return;
  run.state = "authExpired";
  run.files.forEach((f) => {
    if (!isPending(f)) return;
    abortParts(f);
    f.status = "error";
    f.error = AUTH_EXPIRED_MESSAGE;
    f.completeQueued = false;
    // The server would answer 401 to an abort as well; the lifecycle rules take over
    f.token = null;
  });
  clearRunTimers(run);
  publishNow(run);
  wake(run);
}

// Every 401 reaches here through the rejection handler of the upload mutation that got it;
// errorLink in Services/ClientConfig.ts only holds Logout() back for those operations.
function expireAllRuns(): void {
  Object.keys(runs).forEach((key) => expireRun(runs[key]));
}

function anyRunActive(): boolean {
  const keys = Object.keys(runs);
  for (let i = 0; i < keys.length; i++) if (isActiveState(runs[keys[i]].state)) return true;
  return false;
}

function onBeforeUnload(e: BeforeUnloadEvent): string | undefined {
  if (!anyRunActive()) return undefined;
  e.preventDefault();
  // Chrome needs a value; the text itself is ignored by every current browser
  e.returnValue = "";
  return "";
}

function onPageHide(): void {
  let tokens: string[] = [];
  Object.keys(runs).forEach((key) => {
    const run = runs[key];
    if (isActiveState(run.state)) tokens = tokens.concat(abortableTokens(run));
  });
  sendKeepaliveAbort(tokens);
}

// A cross-origin POST with Authorization needs a preflight, which may not finish while the
// page unloads. Best effort only: the lifecycle rules are the real cleanup.
function sendKeepaliveAbort(tokens: string[]): void {
  const url = process.env.REACT_APP_BASE_URL;
  const jwt = readJwt();
  const query = ABORT_QUOTE_FILE_UPLOADS.loc ? ABORT_QUOTE_FILE_UPLOADS.loc.source.body : "";
  if (!tokens.length || !url || !jwt || !query || typeof fetch !== "function") return;
  const kept: string[] = [];
  let bytes = 0;
  for (let i = 0; i < tokens.length; i++) {
    bytes += tokens[i].length + 3;
    if (bytes > KEEPALIVE_BODY_LIMIT) break;
    kept.push(tokens[i]);
  }
  try {
    fetch(url, {
      method: "POST",
      keepalive: true,
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({
        operationName: "abortQuoteFileUploads",
        query,
        variables: { uploadTokens: kept },
      }),
    }).catch(() => undefined);
  } catch (e) {
    // fetch itself may throw on a closing page; nothing left to do
  }
}

function onOffline(): void {
  Object.keys(runs).forEach((key) => pauseRun(runs[key]));
}

function onOnline(): void {
  Object.keys(runs).forEach((key) => resumeRun(runs[key]));
  pumpAll();
}

function installPageHooks(): void {
  if (pageHooksInstalled || typeof window === "undefined") return;
  pageHooksInstalled = true;
  window.addEventListener("beforeunload", onBeforeUnload);
  window.addEventListener("pagehide", onPageHide);
  window.addEventListener("offline", onOffline);
  window.addEventListener("online", onOnline);
}

// ---------------------------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------------------------

function createRun(key: RunKey, input: StartRunInput): Run {
  let totalBytes = 0;
  const files: FileState[] = input.entries.map((entry, index) => {
    const path = cleanPath(entry.path || "");
    const size = entry.file.size;
    totalBytes += size;
    const snap: RunFileSnapshot = {
      id: `f${index + 1}`,
      name: entry.file.name || "file",
      path,
      size,
      sent: 0,
      status: "queued",
      error: undefined,
      note: undefined,
    };
    return {
      id: snap.id,
      file: entry.file,
      name: snap.name,
      path,
      size,
      status: "queued",
      error: undefined,
      note: undefined,
      directoryId: null,
      dirResolved: false,
      token: null,
      mode: null,
      contentType: "",
      parts: [],
      inFlight: 0,
      signInFlight: false,
      signAttempts: 0,
      signRetryAt: 0,
      completeAttempts: 0,
      completeRetryAt: 0,
      completeQueued: false,
      reopenCount: 0,
      snap,
    };
  });
  return {
    key,
    quoteId: input.quoteId,
    availableForSupplier: input.availableForSupplier,
    comment: input.comment,
    targetDirectoryId: input.targetDirectoryId,
    files,
    directories: directoryPathsFor(input.entries, input.directories || []),
    directoryIds: {},
    dirsDirty: false,
    state: "running",
    totalBytes,
    completedVersion: 0,
    canceled: false,
    driving: false,
    wakeWaiting: null,
    wakePending: false,
    initInFlight: false,
    initAttempts: 0,
    initRetryAt: 0,
    completeInFlight: false,
    completeTimer: null,
    completeDue: false,
    samples: [],
    snapshot: {
      state: "running",
      files: files.map((f) => f.snap),
      totalBytes,
      sentBytes: 0,
      bytesPerSecond: 0,
      etaSeconds: null,
      doneCount: 0,
      errorCount: 0,
      completedVersion: 0,
    },
    summary: { active: true, errors: 0 },
    publishTimer: null,
    publishDueAt: 0,
  };
}

export function startRun(input: StartRunInput): void {
  const key = runKeyFor(input.quoteId, input.availableForSupplier);
  const existing = runs[key];
  if (existing && isActiveState(existing.state)) {
    console.warn(`[upload] a run is already active for ${key}; ignoring startRun`);
    return;
  }
  // A finished run being replaced takes its failed files' S3 side with it
  if (existing) sendAbort(abortableTokens(existing));
  const run = createRun(key, input);
  runs[key] = run;
  installPageHooks();
  publishNow(run);
  driveRun(run);
}

export function cancelRun(key: RunKey): void {
  const run = runs[key];
  if (!run || run.state === "authExpired") return;
  run.canceled = true;
  const tokens = abortableTokens(run);
  run.files.forEach((f) => {
    if (f.status !== "queued" && f.status !== "uploading" && f.status !== "error") return;
    abortParts(f);
    f.token = null;
    f.status = "canceled";
    f.error = undefined;
  });
  publishNow(run);
  // The driver finishes the completes still in flight, then closes the run
  wake(run);
  sendAbort(tokens);
}

export function retryFailed(key: RunKey): void {
  const run = runs[key];
  if (!run || run.state === "authExpired" || run.canceled) return;
  let any = false;
  run.files.forEach((f) => {
    if (f.status !== "error") return;
    any = true;
    f.error = undefined;
    f.completeAttempts = 0;
    f.completeRetryAt = 0;
    f.signAttempts = 0;
    f.signRetryAt = 0;
    f.parts.forEach((part) => {
      if (part.done) return;
      part.attempts = 0;
      part.retryAt = 0;
      part.resigned = false;
    });
    if (!f.token) {
      // No session (init failed, or the folders did): start this file over, folder included.
      // ensureQuoteFileDirectories recreates a folder deleted meanwhile, so the retry gets a
      // live id instead of failing on the same stale one forever.
      f.parts = [];
      f.mode = null;
      f.status = "queued";
      f.dirResolved = false;
      f.directoryId = null;
      if (f.path) delete run.directoryIds[f.path];
      run.dirsDirty = true;
    } else if (allPartsDone(f)) {
      f.status = "completing";
      f.completeQueued = true;
    } else {
      f.status = "uploading";
    }
  });
  if (!any) return;
  run.initAttempts = 0;
  run.initRetryAt = 0;
  if (run.state === "done") run.state = "running";
  publishNow(run);
  if (run.driving) wake(run);
  else driveRun(run);
}

export function useUploadRun(key: RunKey): RunSnapshot | undefined {
  const subscribe = useCallback(
    (callback: () => void) => subscribeRun(key, callback),
    [key],
  );
  const getSnapshot = useCallback(() => getRunSnapshot(key), [key]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function useUploadSummary(key: RunKey): UploadSummary {
  const subscribe = useCallback(
    (callback: () => void) => subscribeRun(key, callback),
    [key],
  );
  const getSnapshot = useCallback(() => getRunSummary(key), [key]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
